/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

/*
* When adding modules to the namespace, ensure that they are added in alphabetical order according to module name.
*/

// MODULES //

import setReadOnly from '@stdlib/utils/define-read-only-property';


// MAIN //

/**
* Top-level namespace.
*
* @namespace discreteUniform
*/
var discreteUniform = {};

/**
* @name cdf
* @memberof discreteUniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform/cdf}
*/
import cdf from './../../../../base/dists/discrete-uniform/cdf';
setReadOnly( discreteUniform, 'cdf', cdf );

/**
* @name DiscreteUniform
* @memberof discreteUniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform/ctor}
*/
import DiscreteUniform from './../../../../base/dists/discrete-uniform/ctor';
setReadOnly( discreteUniform, 'DiscreteUniform', DiscreteUniform );

/**
* @name entropy
* @memberof discreteUniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform/entropy}
*/
import entropy from './../../../../base/dists/discrete-uniform/entropy';
setReadOnly( discreteUniform, 'entropy', entropy );

/**
* @name kurtosis
* @memberof discreteUniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform/kurtosis}
*/
import kurtosis from './../../../../base/dists/discrete-uniform/kurtosis';
setReadOnly( discreteUniform, 'kurtosis', kurtosis );

/**
* @name logcdf
* @memberof discreteUniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform/logcdf}
*/
import logcdf from './../../../../base/dists/discrete-uniform/logcdf';
setReadOnly( discreteUniform, 'logcdf', logcdf );

/**
* @name logpmf
* @memberof discreteUniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform/logpmf}
*/
import logpmf from './../../../../base/dists/discrete-uniform/logpmf';
setReadOnly( discreteUniform, 'logpmf', logpmf );

/**
* @name mean
* @memberof discreteUniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform/mean}
*/
import mean from './../../../../base/dists/discrete-uniform/mean';
setReadOnly( discreteUniform, 'mean', mean );

/**
* @name median
* @memberof discreteUniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform/median}
*/
import median from './../../../../base/dists/discrete-uniform/median';
setReadOnly( discreteUniform, 'median', median );

/**
* @name mgf
* @memberof discreteUniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform/mgf}
*/
import mgf from './../../../../base/dists/discrete-uniform/mgf';
setReadOnly( discreteUniform, 'mgf', mgf );

/**
* @name pmf
* @memberof discreteUniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform/pmf}
*/
import pmf from './../../../../base/dists/discrete-uniform/pmf';
setReadOnly( discreteUniform, 'pmf', pmf );

/**
* @name quantile
* @memberof discreteUniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform/quantile}
*/
import quantile from './../../../../base/dists/discrete-uniform/quantile';
setReadOnly( discreteUniform, 'quantile', quantile );

/**
* @name skewness
* @memberof discreteUniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform/skewness}
*/
import skewness from './../../../../base/dists/discrete-uniform/skewness';
setReadOnly( discreteUniform, 'skewness', skewness );

/**
* @name stdev
* @memberof discreteUniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform/stdev}
*/
import stdev from './../../../../base/dists/discrete-uniform/stdev';
setReadOnly( discreteUniform, 'stdev', stdev );

/**
* @name variance
* @memberof discreteUniform
* @readonly
* @type {Function}
* @see {@link module:@stdlib/stats/base/dists/discrete-uniform/variance}
*/
import variance from './../../../../base/dists/discrete-uniform/variance';
setReadOnly( discreteUniform, 'variance', variance );


// EXPORTS //

export default discreteUniform;
